namespace $ {

	export type $shm_hitalama_method_source_item = $shm_hitalama_method_def & {
		args: string
		decorator: $shm_hitalama_method_decorator
	}

	export class $shm_hitalama_method_source extends $mol_object {

		runtime() {
			return null as any as $shm_hitalama_method_runtime
		}

		indent( text: string ) {
			return text
				.split( '\n' )
				.map( line => line ? `\t\t${ line }` : '' )
				.join( '\n' )
		}

		decorator_source( decorator: $shm_hitalama_method_decorator ) {
			return decorator
		}

		source_item( method: $shm_hitalama_method_def ): $shm_hitalama_method_source_item {
			const runtime = this.runtime()
			return {
				... method,
				name: runtime.method_name( method ) ?? method.name,
				args: runtime.method_args( method ).join( ', ' ),
				decorator: runtime.method_decorator( method ),
			}
		}

		source_method( item: $shm_hitalama_method_source_item ) {
			const decorator = this.decorator_source( item.decorator )
			return `method.${ decorator }( ${ JSON.stringify( item.name ) }, function( ${ item.args } ) {\n${ this.indent( item.code ) }\n} )`
		}

		source() {
			const methods = this.runtime().methods()
				.map( method => this.source_method( this.source_item( method ) ) )
				.join( '\n\n' )

			return `${ methods }\n`
		}

		@ $mol_action
		parse( source: string ) {
			const items = [] as $shm_hitalama_method_source_item[]
			let pos = 0

			while( true ) {
				const start = source.indexOf( 'method.', pos )
				if( start === -1 ) break

				const parsed = this.parse_at( source, start )
				if( !parsed ) {
					pos = start + 7
					continue
				}

				items.push( parsed.item )
				pos = parsed.end
			}
			
			return items
		}

		parse_at( source: string, start: number ) {
			let pos = start + 'method.'.length
			const decorator_name = this.read_ident( source, pos )
			if( !decorator_name ) return null
			pos = decorator_name.end

			const decorator = this.parse_decorator_name( decorator_name.value )
			pos = this.skip_ws( source, pos )
			if( source[ pos ] !== '(' ) return null
			pos = this.skip_ws( source, pos + 1 )

			const name = this.read_string( source, pos )
			if( !name ) return null
			pos = this.skip_ws( source, name.end )
			if( source[ pos ] !== ',' ) return null
			pos = this.skip_ws( source, pos + 1 )

			if( !source.startsWith( 'function', pos ) ) return null
			pos = this.skip_ws( source, pos + 'function'.length )
			if( source[ pos ] !== '(' ) return null

			const args_end = this.find_pair( source, pos, '(', ')' )
			if( args_end === -1 ) return null
			const args = source.slice( pos + 1, args_end ).trim()
			pos = this.skip_ws( source, args_end + 1 )
			if( source[ pos ] !== '{' ) return null

			const body_end = this.find_pair( source, pos, '{', '}' )
			if( body_end === -1 ) return null

			return {
				item: {
					name: name.value,
					args,
					code: this.body_text( source.slice( pos + 1, body_end ) ),
					decorator,
				},
				end: body_end + 1,
			}
		}

		parse_decorator_name( name: string ): $shm_hitalama_method_decorator {
			return this.$.$shm_hitalama_method_decorator( name )
		}

		body_text( text: string ) {
			text = text
				.replace( /^\n/, '' )
				.replace( /\n$/, '' )

			return text
				.split( '\n' )
				.map( line => line.startsWith( '\t' ) ? line.slice( 1 ) : line )
				.join( '\n' )
		}

		skip_ws( source: string, pos: number ) {
			while( /\s/.test( source[ pos ] ?? '' ) ) ++ pos
			return pos
		}

		read_ident( source: string, pos: number ) {
			const match = /^[A-Za-z_$][\w$]*/.exec( source.slice( pos ) )
			if( !match ) return null
			return {
				value: match[ 0 ],
				end: pos + match[ 0 ].length,
			}
		}

		read_string( source: string, pos: number ) {
			const quote = source[ pos ]
			if( quote !== '\'' && quote !== '"' ) return null

			let end = pos + 1
			while( end < source.length ) {
				const char = source[ end ]
				if( char === '\\' ) {
					end += 2
					continue
				}
				if( char === quote ) {
					return {
						value: source.slice( pos + 1, end ),
						end: end + 1,
					}
				}
				++ end
			}

			return null
		}

		find_pair( source: string, pos: number, open: string, close: string ) {
			let depth = 0
			let quote = ''

			for( let index = pos; index < source.length; ++ index ) {
				const char = source[ index ]
				const next = source[ index + 1 ]

				if( quote ) {
					if( char === '\\' ) {
						++ index
						continue
					}
					if( char === quote ) quote = ''
					continue
				}

				if( char === '\'' || char === '"' || char === '`' ) {
					quote = char
					continue
				}

				if( char === '/' && next === '/' ) {
					index = source.indexOf( '\n', index )
					if( index === -1 ) return -1
					continue
				}

				if( char === '/' && next === '*' ) {
					index = source.indexOf( '*/', index + 2 )
					if( index === -1 ) return -1
					++ index
					continue
				}

				if( char === open ) ++ depth
				if( char === close ) {
					-- depth
					if( depth === 0 ) return index
				}
			}

			return -1
		}

		@ $mol_action
		sync_from_source( source: string ) {
			const items = this.parse( source )
			this.runtime().method_import_start( items )
			try {
				items.forEach( ( item, index )=> this.runtime().method_save( item, index ) )
			} finally {
				this.runtime().method_import_end()
			}
		}

	}

}
